/**
 * The session DTOs of Phase 2b (CS r5a deliverable 144; DECISIONS 212, 219 and 221): the run sheet
 * (`session_item`), attendance (`session_attendance`), a session's captures (`capture` with its
 * `session_id` and `seq`), Tonight, and the lifecycle's Start and Finish. 1.T's `SessionV1` and
 * `sessions.*` are unchanged; 2.C's server implements the routes (`routes/sessions-2b.ts`).
 *
 * CS r5b (deliverable 163) adds a session's source files (`session_source`), a capture's promote
 * and its resolution, and the reveals read.
 */
import { TimePointV1 } from '../calendar/time-point.js';
import { CaptureKind, CaptureStatus, ClockReason, PlayRecordState, SessionItemKind, SessionSourceKind, SessionSourceStatus, TimeUnit, } from '../domain/enums.js';
import { InboxCaptureText } from '../inbox/categories.js';
import { z } from '../zod.js';
import { DatedPointV1 } from './chronology.js';
import { IsoTimestamp, RowVersion, Uuid } from './common.js';
import { DocTitle, TypeKey } from './documents.js';
import { KnowledgeEventV1, SecretV1, SessionV1 } from './ledger.js';
import { DocRefV1 } from './links.js';
import { BaseRvQueryV1 } from './query.js';
/** The most items a run sheet holds, and so `sessions.items.list` answers (a bounded list). */
export const SESSION_ITEMS_MAX = 500;
/** The most captures `sessions.captures.list` answers, by `seq` (a bounded list). */
export const SESSION_CAPTURES_MAX = 500;
/** The most rows an attendance holds or a put sends. */
export const SESSION_ATTENDANCE_MAX = 50;
/** The most documents a capture names. */
export const SESSION_CAPTURE_DOCS_MAX = 20;
/** An item's estimated minutes: 0 to 600, as 2.C's server checks (DECISIONS 221 ruling 4). */
export const SESSION_ITEM_MINUTES_MAX = 600;
/**
 * The headings of the session template that 2.C reads (DECISIONS 212 item 8): Tonight's Strong
 * start and Last time's Recap. CS r5b seeds them in the template.
 */
export const SESSION_TEMPLATE_HEADINGS = ['Strong start', 'Recap'];
/** Rendered HTML of a heading's subtree (2.C s2 renders it with doc-model's `render`). */
const Html = z.string().max(200_000);
const Label = z.string().max(500);
const PlannedNote = z.string().max(2000);
const OutcomeNote = z.string().max(10_000);
const Minutes = z.number().int().min(0).max(SESSION_ITEM_MINUTES_MAX);
/** `:id` of a session and `:itemId` of one of its items. */
export const SessionItemParams = z.strictObject({ id: Uuid, itemId: Uuid });
/** `:id` of a session and `:captureId` of one of its captures. */
export const SessionCaptureParams = z.strictObject({ id: Uuid, captureId: Uuid });
const has = (value) => value !== undefined && value !== null;
/**
 * Migration 0014's shape check, for a create: a `scene` names a document and its heading block, a
 * `document` a document only, a `secret` a secret only, a `note` none of them.
 */
function createShape(item, ctx) {
    const want = {
        scene: [true, true, false],
        document: [true, false, false],
        secret: [false, false, true],
        note: [false, false, false],
    };
    const [doc, block, secret] = want[item.kind];
    const got = [has(item.documentId), has(item.blockId), has(item.secretId)];
    const paths = ['documentId', 'blockId', 'secretId'];
    for (const [i, wanted] of [doc, block, secret].entries()) {
        if (got[i] !== wanted) {
            ctx.addIssue({
                code: 'custom',
                message: `a ${item.kind} item ${wanted ? 'names' : 'does not name'} ${paths[i]}`,
                path: [paths[i]],
            });
        }
    }
}
/**
 * The shape as `sessions.items.list` answers it: as for a create, except that a `secret` item
 * carries its secret's `blockId` and `document` (DECISIONS 219 ruling 1), null once the secret is
 * orphaned, while its own `documentId` stays null.
 */
function itemShape(item, ctx) {
    if (item.kind === 'secret') {
        if (!has(item.secretId) || has(item.documentId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'a secret item names its secret only',
                path: ['secretId'],
            });
        }
        return;
    }
    createShape(item, ctx);
    if (has(item.document) !== has(item.documentId)) {
        ctx.addIssue({
            code: 'custom',
            message: 'document is the item’s document',
            path: ['document'],
        });
    }
}
/**
 * One row of a run sheet (`session_item`): a scene (a heading of a document), a document, a secret
 * or a note, in `position` order (a fractional key, as `link.position`). `playState` and
 * `outcomeNote` are derived by 1.M-1's `play` deriver from the item's play records; `anchorLost`
 * is set by 2.C s1 when a scene's heading block is gone (DECISIONS 219 ruling 10). `document` may
 * be a reference document (an SRD monster on the sheet).
 */
export const SessionItemV1 = z
    .strictObject({
    id: Uuid,
    rv: RowVersion,
    sessionId: Uuid,
    kind: SessionItemKind,
    documentId: Uuid.nullable(),
    blockId: Uuid.nullable(),
    secretId: Uuid.nullable(),
    document: DocRefV1.nullable(),
    position: z.string().min(1).max(200),
    label: Label.nullable(),
    plannedNote: PlannedNote.nullable(),
    estMinutes: Minutes.nullable(),
    pinned: z.boolean(),
    playState: PlayRecordState,
    outcomeNote: OutcomeNote.nullable(),
    anchorLost: z.boolean(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
})
    .superRefine(itemShape);
/**
 * `sessions.items.create`: the item's kind and what it names, its label, planned note and
 * minutes, and where it goes: after `afterItemId`, first when that is null, last when it is
 * absent.
 */
export const SessionItemCreateV1 = z
    .strictObject({
    kind: SessionItemKind,
    documentId: Uuid.optional(),
    blockId: Uuid.optional(),
    secretId: Uuid.optional(),
    label: Label.optional(),
    plannedNote: PlannedNote.optional(),
    estMinutes: Minutes.optional(),
    afterItemId: Uuid.nullable().optional(),
})
    .superRefine(createShape);
/** `sessions.items.update`: the label, planned note and minutes (null clears one), and the pin. */
export const SessionItemUpdateV1 = z.strictObject({
    baseRv: RowVersion,
    label: Label.nullable().optional(),
    plannedNote: PlannedNote.nullable().optional(),
    estMinutes: Minutes.nullable().optional(),
    pinned: z.boolean().optional(),
});
/** `sessions.items.move`: after `afterItemId`, or first when it is null. */
export const SessionItemMoveV1 = z.strictObject({
    baseRv: RowVersion,
    afterItemId: Uuid.nullable(),
});
/** `sessions.items.remove`'s `?baseRv=` (a soft delete). */
export const SessionItemRemoveQueryV1 = BaseRvQueryV1;
/** One player character's attendance at a session. */
export const SessionAttendanceRowV1 = z.strictObject({
    pc: DocRefV1,
    present: z.boolean(),
    rv: RowVersion,
});
/** A session's attendance (`session_attendance`), one row per player character. */
export const SessionAttendanceV1 = z.strictObject({
    sessionId: Uuid,
    rows: z.array(SessionAttendanceRowV1).max(SESSION_ATTENDANCE_MAX),
});
/** `sessions.attendance.put`: the whole set, which replaces the stored one. */
export const SessionAttendancePutV1 = z.strictObject({
    rows: z.array(z.strictObject({ pcId: Uuid, present: z.boolean() })).max(SESSION_ATTENDANCE_MAX),
});
/** The actions of `sessions.captures.promote` (CS r5b deliverable 163), by `action.kind`. */
export const CAPTURE_PROMOTE_KINDS = [
    'stub',
    'link',
    'reveal',
    'event',
    'advance',
    'dismiss',
];
export const CapturePromoteKind = z.enum(CAPTURE_PROMOTE_KINDS);
/** A reveal's decision: the tentative assertion confirmed, or rejected (a `retract`). */
export const REVEAL_DECISIONS = ['confirm', 'reject'];
/**
 * What a promote wrote (CS r5b deliverable 163; 2.C s3): the action's kind, when, and the ids of
 * the rows it wrote: a stub's document, the linked or event document, the knowledge event of a
 * decided reveal, the event's placement, the clock advance. Absent ids were not written; a
 * dismissal writes none. A decided reveal also records its decision, `confirm` or `reject` (CS r6c
 * addendum item 4), so After can say which; it is absent on every other kind and on resolutions
 * written before it.
 */
export const CaptureResolutionV1 = z.strictObject({
    kind: CapturePromoteKind,
    at: IsoTimestamp,
    documentId: Uuid.optional(),
    revealDecision: z.enum(REVEAL_DECISIONS).optional(),
    knowledgeEventId: Uuid.optional(),
    placementId: Uuid.optional(),
    clockAdvanceId: Uuid.optional(),
});
/**
 * A capture made during a session (`capture` with its `session_id`): `seq` is the server's,
 * unique within the session (2.C s1 assigns it under the session's row lock; open point 38).
 * `resolution` is r5a's null until a promote or dismissal closes the capture (CS r5b).
 */
export const SessionCaptureV1 = z.strictObject({
    id: Uuid,
    rv: RowVersion,
    sessionId: Uuid,
    seq: z.number().int().min(1),
    kind: CaptureKind,
    body: InboxCaptureText,
    docIds: z.array(Uuid).max(SESSION_CAPTURE_DOCS_MAX),
    at: DatedPointV1.nullable(),
    atReal: IsoTimestamp,
    status: CaptureStatus,
    resolution: CaptureResolutionV1.nullable(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/** `sessions.captures.create`: the kind, the text, the documents it names and its in-world time. */
export const SessionCaptureCreateV1 = z.strictObject({
    kind: CaptureKind,
    body: InboxCaptureText,
    docIds: z.array(Uuid).max(SESSION_CAPTURE_DOCS_MAX).optional(),
    at: TimePointV1.optional(),
});
/** `sessions.captures.update`: any of the create's fields, guarded by `baseRv`. */
export const SessionCaptureUpdateV1 = z.strictObject({
    baseRv: RowVersion,
    kind: CaptureKind.optional(),
    body: InboxCaptureText.optional(),
    docIds: z.array(Uuid).max(SESSION_CAPTURE_DOCS_MAX).optional(),
    at: TimePointV1.nullable().optional(),
});
/** `sessions.captures.list`'s query: every live capture, or those in one status. */
export const SessionCapturesQueryV1 = z.strictObject({ status: CaptureStatus.optional() });
/** `sessions.tonight`'s query: a session, or the next one (2.C ruling 9). */
export const TonightQueryV1 = z.strictObject({ sessionId: Uuid.optional() });
/** How a suggestion is tied to the run sheet's documents. */
export const TONIGHT_SUGGESTION_VIAS = ['mention', 'link', 'embed'];
const Suggestions = z
    .array(z.strictObject({ doc: DocRefV1, via: z.enum(TONIGHT_SUGGESTION_VIAS) }))
    .max(25);
/**
 * Tonight (DECISIONS 212): the session (null for the empty state), the next free number (decision
 * 15), Last time (the previous session's Recap heading subtree, rendered by 2.C s2), the Strong
 * start subtree, who is coming, the run sheet with its planned minutes, the secrets ready to
 * reveal, and suggestions from the run sheet's documents' mentions, links and embeds, reference
 * documents included.
 */
export const TonightV1 = z.strictObject({
    session: SessionV1.nullable(),
    nextNumber: z.number().int().min(1),
    lastTime: z
        .strictObject({
        session: DocRefV1,
        number: z.number().int().min(1),
        recapHtml: Html.nullable(),
    })
        .nullable(),
    strongStart: z.strictObject({ blockId: Uuid, html: Html }).nullable(),
    characters: z
        .array(z.strictObject({ pc: DocRefV1, present: z.boolean() }))
        .max(SESSION_ATTENDANCE_MAX),
    items: z.array(SessionItemV1).max(SESSION_ITEMS_MAX),
    plannedMinutes: z.number().int().min(0),
    secretsReady: z.array(SecretV1).max(100),
    suggestions: z.strictObject({
        npcs: Suggestions,
        monsters: Suggestions,
        locations: Suggestions,
        loot: Suggestions,
    }),
});
/** `sessions.start`: the session's number, the next free one when absent (decision 15). */
export const SessionStartV1 = z.strictObject({ number: z.number().int().min(1).optional() });
/** `sessions.finish`'s answer: the processed session and the next one, planned at NOW. */
export const SessionFinishedV1 = z.strictObject({ session: SessionV1, next: SessionV1 });
// ------------------------------------------------------------------- CS r5b: promote, sources
/** Each promote action's own fields (strict: an action of one kind refuses another's fields). */
export const CapturePromoteActionV1 = z.discriminatedUnion('kind', [
    /** A stub document of the type, in the canon library (never `session`). */
    z.strictObject({ kind: z.literal('stub'), title: DocTitle, typeKey: TypeKey }),
    /** A live document, canon or reference, joins the capture's documents. */
    z.strictObject({ kind: z.literal('link'), documentId: Uuid }),
    /** A tentative assertion of this session confirmed or rejected. */
    z.strictObject({
        kind: z.literal('reveal'),
        knowledgeEventId: Uuid,
        decision: z.enum(REVEAL_DECISIONS),
    }),
    /** A canon event on a live canon document, dated as sent, else at the capture's time, else NOW. */
    z.strictObject({
        kind: z.literal('event'),
        documentId: Uuid,
        summary: z.string().min(1).max(10_000).optional(),
        at: TimePointV1.optional(),
    }),
    /** A clock advance from the capture (`captureId` is the capture's; one per capture). */
    z.strictObject({
        kind: z.literal('advance'),
        deltaN: z.number().int().min(1),
        deltaUnit: TimeUnit,
        reason: ClockReason,
        clockBaseRv: z.number().int().min(0),
    }),
    z.strictObject({ kind: z.literal('dismiss') }),
]);
/** `sessions.captures.promote`: the capture's `baseRv` and one action. */
export const CapturePromoteV1 = z.strictObject({
    baseRv: RowVersion,
    action: CapturePromoteActionV1,
});
/** `sessions.captures.promote`'s answer: the closed capture and what the action wrote. */
export const CapturePromotedV1 = z.strictObject({
    capture: SessionCaptureV1,
    resolution: CaptureResolutionV1,
});
/**
 * A source file's extensions by kind (amendments 2.1 section 3; DECISIONS 221): the last
 * extension, lower case, decides; nothing is sniffed. `other` takes their union.
 */
const GMA_SUMMARY_EXTENSIONS = ['md', 'markdown', 'html', 'htm', 'txt', 'yaml', 'yml'];
const TRANSCRIPT_EXTENSIONS = ['vtt', 'srt', 'json', 'txt'];
const FOUNDRY_CHAT_EXTENSIONS = ['json', 'txt'];
export const SESSION_SOURCE_EXTENSIONS = {
    gma_summary: GMA_SUMMARY_EXTENSIONS,
    transcript: TRANSCRIPT_EXTENSIONS,
    foundry_chat: FOUNDRY_CHAT_EXTENSIONS,
    other: [
        ...new Set([...GMA_SUMMARY_EXTENSIONS, ...TRANSCRIPT_EXTENSIONS, ...FOUNDRY_CHAT_EXTENSIONS]),
    ],
};
/** Every extension a source may have, in `SESSION_SOURCE_EXTENSIONS.other`'s order. */
export const SESSION_SOURCE_FORMATS = SESSION_SOURCE_EXTENSIONS.other;
/** The largest source file (DECISIONS 212 item 9): 25 MiB. */
export const SESSION_SOURCE_MAX_BYTES = 26_214_400;
/**
 * The media types `sessions.sources.upload` accepts. Browsers send `application/octet-stream` (or
 * nothing) for `.vtt`, `.srt` and `.md`, so the extension decides the kind, never the type.
 */
export const SESSION_SOURCE_MEDIA_TYPES = [
    'application/octet-stream',
    'text/plain',
    'text/markdown',
    'text/html',
    'text/vtt',
    'application/x-subrip',
    'application/json',
];
/** The extension of `fileName` (the last one, lower case), or null without one. */
export function sourceExtensionOf(fileName) {
    const base = fileName.slice(Math.max(fileName.lastIndexOf('/'), fileName.lastIndexOf('\\')) + 1);
    const dot = base.lastIndexOf('.');
    if (dot <= 0 || dot === base.length - 1)
        return null;
    return base.slice(dot + 1).toLowerCase();
}
/** Whether a file named `fileName` may be a source of `kind` (its extension is in the kind's list). */
export function sourceExtensionAllowed(kind, fileName) {
    const ext = sourceExtensionOf(fileName);
    return ext !== null && SESSION_SOURCE_EXTENSIONS[kind].includes(ext);
}
/**
 * A session's source file (`session_source`, Phase 2's columns; A2.1 section 3): its kind as the
 * GM chose it, its format (the extension), its status (`uploaded` until Phase 3 parses it) and the
 * stored file. Nothing reads the bytes in Phase 2.
 */
export const SessionSourceV1 = z
    .strictObject({
    id: Uuid,
    rv: RowVersion,
    sessionId: Uuid,
    kind: SessionSourceKind,
    format: z.enum(SESSION_SOURCE_FORMATS),
    status: SessionSourceStatus,
    asset: z.strictObject({
        assetId: Uuid,
        bytes: z.number().int().min(0).max(SESSION_SOURCE_MAX_BYTES),
        originalName: z.string().min(1).max(255),
    }),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
})
    .superRefine((source, ctx) => {
    if (!SESSION_SOURCE_EXTENSIONS[source.kind].includes(source.format)) {
        ctx.addIssue({
            code: 'custom',
            message: `a ${source.kind} source is never ${source.format}`,
            path: ['format'],
        });
    }
});
/** The most sources `sessions.sources.list` answers (a bounded list, newest first). */
export const SESSION_SOURCES_MAX = 100;
/** `sessions.sources.upload`'s form fields beside the file: the kind the GM chose. */
export const SessionSourceUploadFieldsV1 = z.strictObject({ kind: SessionSourceKind });
/** `:id` of a session and `:sourceId` of one of its sources. */
export const SessionSourceParams = z.strictObject({ id: Uuid, sourceId: Uuid });
/** The most reveals `sessions.reveals.list` answers. */
export const SESSION_REVEALS_MAX = 200;
/**
 * One of a session's tentative assertions that nothing supersedes (`sessions.reveals.list`; 2.E's
 * Undo after a reload, 2.F's After tab): the event, a label for it (the secret's label, else the
 * document's title), the document it is on, and whether it was confirmed or rejected since.
 */
export const SessionRevealV1 = z.strictObject({
    event: KnowledgeEventV1,
    label: z.string().max(500),
    documentId: Uuid,
    decided: z.boolean(),
});
//# sourceMappingURL=sessions.js.map